import { faBarcode } from "@fortawesome/free-solid-svg-icons/faBarcode";
import { faSearch } from "@fortawesome/free-solid-svg-icons/faSearch";
import { faTimesCircle } from "@fortawesome/free-solid-svg-icons/faTimesCircle";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { InputMascara } from "app/components/inputs/InputMascara";
import { InputNumerico } from "app/components/inputs/InputNumerico";
import { useFormik } from "formik";
import React, { lazy, memo, Suspense, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import globalMU from "globalMU";
import { PaginaPesquisaProdutoContext } from "../pagina-pesquisa-produto/PaginaPesquisaProduto";

import { carregarModalLeitorCodigoBarras } from "./modalLeitorCodigoBarras/carregarModalLeitorCodigoBarras";

// lazy: zxing (~1,3MB) so deve entrar no bundle quando o modal de leitura de codigo de barras for aberto.
// O import() de verdade fica no .js (carregarModalLeitorCodigoBarras) para o webpack conseguir
// gerar um chunk assincrono separado - ver comentario naquele arquivo.
const ModalLeitorCodigoBarras = lazy(() =>
  carregarModalLeitorCodigoBarras().then((m) => ({ default: m.ModalLeitorCodigoBarras }))
);
const estiloPesquisa = require("./pesquisa-produto.module.scss").default;
const estilosInputs = require("../../../estilos/inputs.module.scss").default;

const opcoesOrdenacaoPadrao = [
  { value: "ALFABETICA", label: "Alfabética" },
  { value: "CODIGO", label: "Código" },
  { value: "CLASSE", label: "Classe" },
  { value: "CODIGOFABRICANTE", label: "Código Fabricante" },
  { value: "FORNECEDOR", label: "Fornecedor" }, // 489210
];

const normalizarTextoColado = (texto: string) =>
  texto.trimStart().replace(/[\t\n\r]/g, "");

export type DadosPesquisaProduto = {
  Classe: string;
  Referencia: string;
  ClasseProduto: string;
  Codigo: string;
  CodigoFabricante: string;
  Descricao: string;
  MarkupFinal?: string;
  MarkupInicial?: string;
  EstoqueDisponivelFilialCorrente: boolean;
  EstoqueDisponivelOutrasFiliais: boolean;
  EstoqueDisponivelCD: boolean; // 435588
  Fornecedor: string;
  NomeFornecedor: string;
  Ordem: string;

};

export const PesquisaProduto = memo(() => {
  const contexto = useContext(PaginaPesquisaProdutoContext)
  const [abrirModalCodigoBarras, setAbrirModalCodigoBarras] = useState(false)
  const isMounted = useRef(true)

  useEffect(() => {
    isMounted.current = true

    return () => {
      isMounted.current = false
    }
  }, [])

  const initialValues = useMemo(() => ({
      ...contexto.pesquisaProduto,
      Codigo: contexto.pesquisaProduto.Codigo || "", // resolver problema [A component is changing an uncontrolled input to be controlled]
      Referencia: contexto.pesquisaProduto.Referencia || "",
      Descricao: contexto.pesquisaProduto.Descricao || "",
      Classe: contexto.pesquisaProduto.Classe || "",
      Fornecedor: contexto.pesquisaProduto.Fornecedor || "",
      NomeFornecedor: contexto.pesquisaProduto.NomeFornecedor || "",
      MarkupInicial: contexto.pesquisaProduto.MarkupInicial || "",
      MarkupFinal: contexto.pesquisaProduto.MarkupFinal || "",
      EstoqueDisponivelFilialCorrente: contexto.pesquisaProduto.EstoqueDisponivelFilialCorrente || false,
      EstoqueDisponivelOutrasFiliais: contexto.pesquisaProduto.EstoqueDisponivelOutrasFiliais || false,
      EstoqueDisponivelCD: contexto.pesquisaProduto.EstoqueDisponivelCD || false, // 435588
      Ordem: contexto.pesquisaProduto.Ordem || "ALFABETICA",
  }), [contexto.pesquisaProduto]);

  const formik = useFormik({
    initialValues,
    onSubmit: (values, { }) => { },
    enableReinitialize: true,
  });

  const opcoesOrdenacao = useMemo(
    () => contexto.exibirColunaEstoqueTotalProdutoVendas
      ? opcoesOrdenacaoPadrao.concat({ value: "QUANTIDADE", label: "Quantidade" })
      : opcoesOrdenacaoPadrao,
    [contexto.exibirColunaEstoqueTotalProdutoVendas]
  );

  const pesquisarSeEnterPressionado = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key == "Enter") {
      contexto.onPesquisarPaginaPesquisaProduto()
    }
  }, [contexto.onPesquisarPaginaPesquisaProduto])

  const abrirLeitorCodigoBarras = useCallback(() => {
    if (!isMounted.current || contexto.processandoProduto) return;

    setAbrirModalCodigoBarras(true);
  }, [contexto.processandoProduto]);

  const fecharLeitorCodigoBarras = useCallback(() => {
    if (!isMounted.current) return;

    setAbrirModalCodigoBarras(false);
  }, []);

  const onCodigoBarrasLido = useCallback((codigoBarras) => {
    if (!isMounted.current) return;

    const codigo = codigoBarras?.getText();
    if (!codigo) return;

    formik.setFieldValue("Referencia", codigo);
    contexto.onReferenciaModificado(codigo);
  }, [formik.setFieldValue, contexto.onReferenciaModificado]);

  const codigo = <div className={estiloPesquisa.tamanho2}>
    <label
      title="Tecla de atalho [O]"
    >Códig<u>o</u></label>
    <input
      className={estilosInputs.inputPadrao}
      disabled={contexto.processandoProduto}
      name="Codigo"
      id="PesquisaProdutos_Codigo"
      autoComplete="off"
      list="autocompleteOff"
      accessKey="o"
      title="Tecla de atalho [O]"
      maxLength={5}
      value={(formik.values?.Codigo) ?? ""}
      onPaste={e => {
          e.preventDefault()
          const data = normalizarTextoColado(e.clipboardData.getData('text'))

          contexto.onCodigoModificado(data)
      }}
      onKeyDown={pesquisarSeEnterPressionado}
      onChange={e => {
        formik.handleChange(e)
        contexto.onCodigoModificado(e.target.value)

      }}

    />
  </div>

  const referencia = <div className={estiloPesquisa.tamanho3}>
    <label
      title="Tecla de atalho [i]"
    >Referênc<u>i</u>a</label>
    <input
      className={estilosInputs.inputPadrao}
      disabled={contexto.processandoProduto}
      name="Referencia"
      id="PesquisaProdutos_Referencia"
      autoComplete="off"
      list="autocompleteOff"
      accessKey="i"
      title="Tecla de atalho [i]&#0013;Ordem de verificação dos parâmetros:&#0013;1º - Chave de movimentação do orçamento&#0013;2º - Chave de movimentação no Frente de Loja&#0013;3º - Chave de Movimentação do Estoque"
      maxLength={30}
      value={(formik.values?.Referencia) ?? ""}
      onPaste={e => {
          e.preventDefault()
          const data = normalizarTextoColado(e.clipboardData.getData('text'))

          contexto.onReferenciaModificado(data)
      }}
      onKeyDown={pesquisarSeEnterPressionado}
      onChange={e => {
        formik.handleChange(e)
        contexto.onReferenciaModificado(e.target.value)
      }}
      // Sem o "&& !contexto.modalCestaProdutosAberto": quando a página é montada já com o
      // modal da cesta aberto (491123 — botão flutuante fora da tela de pesquisa), o atributo
      // autoFocus nativo rouba o foco do Dialog no mesmo commit em que o MUI marca o restante
      // da árvore com aria-hidden, disparando "Blocked aria-hidden on an element because its
      // descendant retained focus".
      autoFocus={contexto.autoFocusReferencia && !contexto.modalCestaProdutosAberto}
      ref={contexto.autoFocusReferencia ? contexto.inputAutofocusRef : null}

    />
    <FontAwesomeIcon
      className={estiloPesquisa.icone_pesquisar}
      icon={faBarcode}
      id="PesquisaProdutos_Referencia_IconeLeitorCodigoBarras"
      key="PesquisaProdutos_Referencia_IconeLeitorCodigoBarras"
      cursor={"pointer"}
      onClick={abrirLeitorCodigoBarras}
      title={"É Preciso uma câmera com permissão para a funcionalidade de escanear"}
    />
  </div>

  const descricao = <div className={estiloPesquisa.tamanho3}>
    <label
      title="Tecla de atalho [S]"
    >De<u>s</u>crição</label>
    <input
      className={estilosInputs.inputPadrao}
      disabled={contexto.processandoProduto}
      name="Descricao"
      id="PesquisaProdutos_Descricao"
      accessKey="s"
      title="Tecla de atalho [S]"
      maxLength={100}
      value={(formik.values?.Descricao) ?? ""}
      onKeyDown={pesquisarSeEnterPressionado}
      onChange={e => {
        formik.handleChange(e)
        contexto.onDescricaoModificado(e.target.value)
      }}
      onBlur={(e) => contexto.onDescricaoModificado(e.target.value)}
      // Ver comentário equivalente no autoFocus de PesquisaProdutos_Referencia acima (491123).
      autoFocus={!contexto.autoFocusReferencia && !contexto.modalCestaProdutosAberto}
      ref={!contexto.autoFocusReferencia ? contexto.inputAutofocusRef : null}
    />
  </div>

  const classe = <>{!contexto.ocultarSubPaginas &&
  <div className={estiloPesquisa.tamanho3}>
    <label>Classe</label>
    <InputMascara
      mascara={contexto.mascara}
      disabled={contexto.processandoProduto}
      valor={formik.values?.Classe ?? ""}
      onValorModificado={classe => {
        formik.setFieldValue("Classe", classe ?? "")
        contexto.onClasseModificado(classe ?? "")

      }}
      onEnterPressionado={() => {
        contexto.onPesquisarPaginaPesquisaProduto()
      }}
      emitirValorDeFormaSincrona={true}
      permitirMascaraIncompleta={true}

    />
    <FontAwesomeIcon
      className={estiloPesquisa.icone_pesquisar}
      icon={faSearch}
      cursor={"pointer"}
      onClick={() => contexto.onPesquisaClasse()}
    />
    <FontAwesomeIcon
      className={estiloPesquisa.icone_excluir}
      icon={faTimesCircle}
      cursor={"pointer"}
      onClick={() => contexto.onExcluirClasse()}
    />
  </div>
}</>

  const fornecedor = <>{!contexto.ocultarSubPaginas &&
  <div className={estiloPesquisa.tamanho3}>
    <label>Fornecedor</label>
    <input
      name="Fornecedor"
      disabled={contexto.processandoProduto}
      type={"hidden"}
      value={(formik.values.Fornecedor) ?? ""}
    />
    <input
      className={estilosInputs.inputPadrao}
      placeholder="Clique na lupa para pesquisar"
		  title="Clique na lupa para pesquisar o fornecedor"
      name="NomeFornecedor"
      id="PesquisaProdutos_NomeFornecedor"
      disabled // sempre fica desabilitado
      value={(formik.values?.NomeFornecedor) ?? ""}
      onChange={formik.handleChange}
      onBlur={(e) => contexto.onFornecedorModificado(e.target.value)}
      style={{ borderColor: "#e4e6e8" }}

    />
    <FontAwesomeIcon
      className={estiloPesquisa.icone_pesquisar}
      icon={faSearch}
      cursor={"pointer"}
      onClick={() => contexto.onPesquisaFornecedor()}
    />
    <FontAwesomeIcon
      className={estiloPesquisa.icone_excluir}
      icon={faTimesCircle}
      cursor={"pointer"}
      onClick={() => contexto.onExcluirFornecedor()}
    />
  </div>
}</>

  const markup = contexto.markupProdutoOrcamento && (
    <div className={estiloPesquisa.containerMarkup}>
      <div className={estiloPesquisa.tamanho1}>
        <label>% Markup Inicial</label>
        <InputNumerico
          id="PesquisaProdutos_ValorInicialMarkup"
          disabled={contexto.processandoProduto}
          onValorModificado={(valor) => {
            formik.setFieldValue("MarkupInicial", valor);
            contexto.onMarkupInicialModificado(valor.toString());
          }}
          valor={
            Number.isNaN(formik.values.MarkupInicial)
              ? Number(formik.values.MarkupInicial)
              : null
          }
          numeroCasasDecimais={2}
          selecionaValorOnFocus={true}
          campoNullavel={true}
        />
      </div>
      <div className={estiloPesquisa.tamanho1}>
        <label>% Markup Final</label>
        <InputNumerico
          id="PesquisaProdutos_ValorFinalMarkup"
          disabled={contexto.processandoProduto}
          onValorModificado={(valor) => {
            formik.setFieldValue("MarkupFinal", valor);
            contexto.onMarkupFinalModificado(valor.toString());
          }}
          valor={
            Number.isNaN(formik.values.MarkupFinal)
              ? Number(formik.values.MarkupFinal)
              : null
          }
          numeroCasasDecimais={2}
          selecionaValorOnFocus={true}
          campoNullavel={true}
        />
      </div>
    </div>
  )

  const filtrosDeEstoque = <>
    {contexto.exibirCampoFiltroEstoqueDisponivel && (
      <div className={estiloPesquisa.checkbox}>
        <label
          style={{ cursor: 'pointer' }}
          title="Estoque disponível na filial corrente"
          htmlFor="PesquisaProdutos_EstoqueDisponivelFilialCorrente">Estoque Disponível</label>
        <input
          type="checkbox"
          name="EstoqueDisponivelFilialCorrente"
          id="PesquisaProdutos_EstoqueDisponivelFilialCorrente"
          title="Estoque disponível na filial corrente"
          disabled={contexto.processandoProduto}
          onChange={(e) => {
            formik.handleChange(e);
            contexto.onEstoqueDisponivelFilialCorrente(e.target.checked);
          }}
          checked={formik.values?.EstoqueDisponivelFilialCorrente ?? false}
        />
      </div>
    )}

    {contexto.exibirCampoPesquisaEstoqueOutrasFiliais && (
      <div className={estiloPesquisa.checkbox}>
        <label
          style={{ cursor: 'pointer' }}
          htmlFor="PesquisaProdutos_EstoqueDisponivelOutrasFiliais"
          title="Veja parâmetro [Exibir Estoque de Outras filiais]">Estoque Disponível Outras Filiais</label>
        <input
          type="checkbox"
          name="EstoqueDisponivelOutrasFiliais"
          id="PesquisaProdutos_EstoqueDisponivelOutrasFiliais"
          title="Veja parâmetro [Exibir Estoque de Outras filiais]"
          disabled={contexto.processandoProduto}
          onChange={(e) => {
            formik.handleChange(e);
            contexto.onEstoqueDisponivelOutrasFiliais(e.target.checked);
          }}
          checked={formik.values?.EstoqueDisponivelOutrasFiliais ?? false}
        />
      </div>
    )}

    <div className={estiloPesquisa.checkbox}>
      <label
        style={{ cursor: 'pointer' }}
        title="Estoque disponível no(s) Centro(s) de Distribuição(ões)"
        htmlFor="PesquisaProdutos_EstoqueDisponivelCD">
        Estoque Disponível CD</label>
      <input
        type="checkbox"
        id="PesquisaProdutos_EstoqueDisponivelCD"
        title="Estoque disponível no(s) Centro(s) de Distribuição(ões)"
        disabled={contexto.processandoProduto}
        onChange={(e) => {
          formik.handleChange(e);
          contexto.onEstoqueDisponivelCD(e.target.checked);
        }}
        checked={formik.values?.EstoqueDisponivelCD ?? false} // 435588
      />
    </div>

    {contexto.exibirMensagemParametroEstoqueOutrasFiliais && (
      <div>
        <p className={estiloPesquisa.mensagem_usuario}>
          Para que o filtro de Estoque disponível outras filiais
          funcione, é necessário ligar o parâmetro de{" "}
          <b>"Exibir Estoque de Outras filiais"</b>
        </p>
      </div>
    )}
  </>

  const ordem = <div>
    <label>Ordem</label>
    <select
      name="Ordem"
      disabled={contexto.processandoProduto}
      className={estiloPesquisa.tamanho3}
      value={(formik.values.Ordem) ?? ""}
      onChange={(e) => {
        formik.handleChange(e);
        contexto.onOrdemModificado(e.target.value);
      }}
    >
      {opcoesOrdenacao.map((opcao) => (
        <option key={opcao.value} value={opcao.value}>
          {opcao.label}
        </option>
      ))}
    </select>
  </div>

  return (
    <div id="pesquisar-produtos-region">
      <form id="form-pesquisar-produto" onSubmit={formik.handleSubmit}>
        <fieldset
          className={estiloPesquisa.fieldsetLabel}
          id="fieldset-pesquisar-produto-filtros"
          disabled={contexto.processandoProduto}
        >
          <div className="_flex _flex-col">
            {codigo}
            {referencia}
            {descricao}
            {classe}
            {fornecedor}

            {markup}

            {filtrosDeEstoque}

            {ordem}
          </div>
        </fieldset>
      </form>
      {abrirModalCodigoBarras &&
    <Suspense fallback={null}>
      <ModalLeitorCodigoBarras
        abrirModal={abrirModalCodigoBarras}
        onFecharModal={fecharLeitorCodigoBarras}
        onCodigoBarrasLido={onCodigoBarrasLido}
      />
    </Suspense>
}
    </div>
  );
})
